import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { lcApiBase, lcHttpError, lcStationId } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface TicketsCounts {
  actif: number;
  archive: number;
}

export interface TicketsPageMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface TicketsPageResult {
  items: Record<string, string | unknown>[];
  pagination: TicketsPageMeta;
}

export interface TicketsListFilters {
  origine?: string;
  reglement?: string;
  statut?: string;
  type?: string;
  /** Sous-types métier des tickets (vente, acompte, échange…). */
  sous_types?: string[];
  /** Famille UI : ticke = vente+avoir(+annule) ; initialisation = en traitement. */
  famille?: string;
  date_debut?: string;
  date_fin?: string;
  facture_client?: boolean;
  exclude_factured?: boolean;
  document_scope?: 'tickets' | 'negoce';
  negoce_kind?: 'facture' | 'avoire';
  /** Ticket compensatoire (annulation tické payé) : total < 0 + document_original_id. */
  ticket_avoire?: boolean;
  /** Onglet Consolidé : consolide = 1 en base. */
  consolide_only?: boolean;
  /** Onglet Tické : exclure les tickets déjà marqués consolidés. */
  exclude_consolide?: boolean;
  /** Onglet Tous : exclure les tickets en traitement (onglet Disponible). */
  exclude_en_traitement?: boolean;
  /** Onglet Tické : exclure les tickets annulés (garder vente + avoir). */
  exclude_annule?: boolean;
}

interface TicketsCountsResponse {
  success: boolean;
  data?: TicketsCounts;
  error?: string;
}

interface TicketsListResponse {
  success: boolean;
  items?: Record<string, string | unknown>[];
  meta?: {
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
  error?: string;
}

interface TicketDetailResponse {
  success: boolean;
  data?: Record<string, unknown>;
  error?: string;
}

interface GenerateSiegeResponse {
  success: boolean;
  batch_key?: string;
  count?: number;
  items?: unknown[];
  error?: string;
}

export interface GenerateSiegeResult {
  batch_key: string;
  count: number;
  items: unknown[];
}

/** Erreur métier 409 : tickets déjà exportés ; relancer avec force: true après confirm. */
export const SIEGE_NEEDS_FORCE = 'SiegeNeedsForceError';

export type SiegeNeedsForceError = Error & {
  name: typeof SIEGE_NEEDS_FORCE;
  siegeOverlapToday: boolean;
  siegeOtherDates: string[];
};

@Injectable({ providedIn: 'root' })
export class LogicielCaisseTicketService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  getTicketsCounts(
    entrepotIds?: number[],
    attrFilters?: TicketsListFilters
  ): Observable<TicketsCounts> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }

    const params = this.buildFilterParams(entrepotIds, attrFilters);
    const url = `${lcApiBase(this.authService)}/api/documents/station/${stationId}/tickets/counts`;
    return this.http.get<TicketsCountsResponse>(url, { params }).pipe(
      map((res) => {
        if (!res?.success || !res.data) {
          throw new Error(res?.error || 'Réponse compteurs tickets invalide');
        }
        return {
          actif: res.data.actif ?? 0,
          archive: res.data.archive ?? 0,
        };
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur compteurs tickets.'))
      )
    );
  }

  getTicketsPage(options: {
    actif: boolean | 'all';
    page?: number;
    limit?: number;
    entrepotIds?: number[];
    attrFilters?: TicketsListFilters;
  }): Observable<TicketsPageResult> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }

    const page = options.page ?? 1;
    const limit = options.limit ?? 10;
    let params = this.buildFilterParams(options.entrepotIds, options.attrFilters);
    const actifParam = options.actif === 'all' ? 'all' : options.actif ? 'true' : 'false';
    params = params
      .set('actif', actifParam)
      .set('page', String(Math.max(1, page)))
      .set('limit', String(Math.min(100, Math.max(1, limit))));

    const url = `${lcApiBase(this.authService)}/api/documents/station/${stationId}/tickets`;
    return this.http.get<TicketsListResponse>(url, { params }).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.items)) {
          throw new Error(res?.error || 'Réponse tickets invalide');
        }
        const m = res.meta ?? {};
        const total = m.total ?? res.items.length;
        const pageRes = m.page ?? page;
        const limitRes = m.limit ?? limit;
        const totalPages = m.totalPages ?? Math.max(1, Math.ceil(total / limitRes));
        return {
          items: res.items,
          pagination: {
            total,
            page: pageRes,
            limit: limitRes,
            totalPages,
          },
        };
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur chargement tickets.'))
      )
    );
  }

  generateSiegeFromTickets(
    documentIds: number[],
    force = false
  ): Observable<GenerateSiegeResult> {
    const url = `${lcApiBase(this.authService)}/api/facturations/generate-siege`;
    const body: { document_ids: number[]; force?: boolean } = {
      document_ids: documentIds,
    };
    if (force) {
      body.force = true;
    }
    return this.http.post<GenerateSiegeResponse>(url, body).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Génération siège invalide');
        }
        return {
          batch_key: String(res.batch_key ?? ''),
          count: res.count ?? 0,
          items: Array.isArray(res.items) ? res.items : [],
        };
      }),
      catchError((err: HttpErrorResponse) => {
        if (
          err.status === 409 &&
          err.error &&
          typeof err.error === 'object' &&
          (err.error as { needs_force?: boolean }).needs_force
        ) {
          const errBody = err.error as {
            error?: string;
            overlap_today?: boolean;
            other_dates?: string[];
          };
          const e = new Error(
            typeof errBody.error === 'string'
              ? errBody.error
              : 'Ces tickets ont déjà été inclus dans un export siège.'
          ) as SiegeNeedsForceError;
          e.name = SIEGE_NEEDS_FORCE;
          e.siegeOverlapToday = !!errBody.overlap_today;
          e.siegeOtherDates = Array.isArray(errBody.other_dates)
            ? errBody.other_dates.map(String)
            : [];
          return throwError(() => e);
        }
        return throwError(() =>
          lcHttpError(err, 'Erreur lors de la génération des fichiers siège.')
        );
      })
    );
  }

  getTicketDetail(documentId: number): Observable<Record<string, unknown>> {
    const url = `${lcApiBase(this.authService)}/api/documents/${documentId}`;
    return this.http.get<TicketDetailResponse>(url).pipe(
      map((res) => {
        if (!res?.success || !res.data) {
          throw new Error(res?.error || 'Ticket introuvable');
        }
        return res.data;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur chargement ticket.'))
      )
    );
  }

  /**
   * @deprecated Utiliser cancelTicketsBatch — les tickets ne se suppriment pas, ils s'annulent.
   */
  deleteTicket(documentId: number): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/documents/${documentId}`;
    return this.http.delete<{ success: boolean; error?: string }>(url).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Suppression impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur suppression ticket.'))
      )
    );
  }

  cancelTicketsBatch(
    documentIds: number[],
    options?: { allowPaidCancel?: boolean; stationId?: number }
  ): Observable<{ cancelled: unknown[] }> {
    const session = this.authService.getSession();
    const url = `${lcApiBase(this.authService)}/api/documents/cancel-batch`;
    const body = {
      document_ids: documentIds,
      allow_paid_cancel: !!options?.allowPaidCancel,
      station_id: options?.stationId ?? this.authService.getBackStationId() ?? null,
    };
    return this.http
      .post<{ success: boolean; data?: { cancelled: unknown[] }; error?: string }>(url, body)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Annulation impossible');
          }
          return { cancelled: res.data.cancelled || [] };
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors de l\'annulation des tickets.'))
        )
      );
  }

  createDraftFactureFromTickets(
    documentIds: number[],
    stationId?: number,
    options?: { markConsolide?: boolean }
  ): Observable<{ facture_id: number; facture_code: string; ticket_ids: number[]; total: number }> {
    const url = `${lcApiBase(this.authService)}/api/documents/facture-batch/draft`;
    const body: {
      document_ids: number[];
      station_id: number | null;
      mark_consolide?: boolean;
    } = {
      document_ids: documentIds,
      station_id: stationId ?? this.authService.getBackStationId() ?? null,
    };
    if (options?.markConsolide) {
      body.mark_consolide = true;
    }
    return this.http
      .post<{
        success: boolean;
        data?: { facture_id: number; facture_code: string; ticket_ids: number[]; total: number };
        error?: string;
      }>(url, body)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Création brouillon facture impossible');
          }
          return res.data;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors de la création du brouillon facture.'))
        )
      );
  }

  prepareErpFactureFromTickets(
    documentIds: number[],
    stationId?: number,
    options?: { markConsolide?: boolean; allowConsolide?: boolean }
  ): Observable<{
    ticket_ids: number[];
    ticket_codes: string[];
    total_ht: number;
    total_ttc: number;
    total_tva: number;
    tva_rate: number;
    client_id: number | null;
    client_type: string;
  }> {
    const url = `${lcApiBase(this.authService)}/api/documents/facture-batch/prepare-erp`;
    const body: {
      document_ids: number[];
      station_id: number | null;
      mark_consolide?: boolean;
      allow_consolide?: boolean;
    } = {
      document_ids: documentIds,
      station_id: stationId ?? this.authService.getBackStationId() ?? null,
    };
    if (options?.markConsolide) {
      body.mark_consolide = true;
    }
    if (options?.allowConsolide) {
      body.allow_consolide = true;
    }
    return this.http
      .post<{
        success: boolean;
        data?: {
          ticket_ids: number[];
          ticket_codes: string[];
          total_ht: number;
          total_ttc: number;
          total_tva: number;
          tva_rate: number;
          client_id: number | null;
          client_type: string;
        };
        error?: string;
      }>(url, body)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Préparation facture ERP impossible');
          }
          return res.data;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors de la préparation de la facture ERP.'))
        )
      );
  }

  /**
   * Lie des tickets à la facture ERP générée (référence + consolidé) après enregistrement.
   * POST /api/documents/facture-batch/link-erp
   */
  linkTicketsToErpFacture(
    documentIds: number[],
    options?: {
      stationId?: number;
      erpFactureId?: number | null;
      erpFactureCode?: string | null;
      markConsolide?: boolean;
    }
  ): Observable<{ ticket_ids: number[] }> {
    const url = `${lcApiBase(this.authService)}/api/documents/facture-batch/link-erp`;
    const body = {
      document_ids: documentIds,
      station_id: options?.stationId ?? this.authService.getBackStationId() ?? null,
      erp_facture_id: options?.erpFactureId ?? null,
      erp_facture_code: options?.erpFactureCode ?? null,
      mark_consolide: options?.markConsolide !== false,
    };
    return this.http
      .post<{ success: boolean; data?: { ticket_ids: number[] }; error?: string }>(url, body)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Liaison tickets → facture impossible');
          }
          return res.data;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors de la liaison des tickets à la facture.'))
        )
      );
  }

  /**
   * Traçabilité : tickets caisse liés à une facture ERP.
   * GET /api/documents/by-erp-facture/:id
   */
  getTicketsByErpFacture(erpFactureId: number): Observable<{
    erp_facture_id: number;
    facture_erp_code: string | null;
    tickets: Record<string, unknown>[];
    count: number;
  }> {
    const id = Number(erpFactureId);
    if (!Number.isFinite(id) || id <= 0) {
      return throwError(() => new Error('erpFactureId invalide'));
    }
    const url = `${lcApiBase(this.authService)}/api/documents/by-erp-facture/${id}`;
    return this.http
      .get<{
        success: boolean;
        data?: {
          erp_facture_id: number;
          facture_erp_code?: string | null;
          tickets?: Record<string, unknown>[];
          count?: number;
        };
        error?: string;
      }>(url)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Réponse tickets consolidés invalide');
          }
          return {
            erp_facture_id: res.data.erp_facture_id ?? id,
            facture_erp_code: res.data.facture_erp_code ?? null,
            tickets: Array.isArray(res.data.tickets) ? res.data.tickets : [],
            count: res.data.count ?? (Array.isArray(res.data.tickets) ? res.data.tickets.length : 0),
          };
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur chargement tickets consolidés.'))
        )
      );
  }

  /**
   * IDs facture ERP ayant au moins un ticket consolidé lié.
   * GET /api/documents/erp-facture-links?ids=
   * Sans ids : tous les IDs liés pour la station.
   */
  getErpFactureLinks(erpFactureIds?: number[]): Observable<number[]> {
    const ids = (erpFactureIds || [])
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
    const url = `${lcApiBase(this.authService)}/api/documents/erp-facture-links`;
    let params = new HttpParams();
    if (ids.length) {
      params = params.set('ids', ids.join(','));
    }
    return this.http
      .get<{ success: boolean; data?: { linked_ids?: number[] }; error?: string }>(url, { params })
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.error || 'Réponse liaisons facture ERP invalide');
          }
          const linked = res.data?.linked_ids;
          return Array.isArray(linked)
            ? linked.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
            : [];
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur chargement liaisons tickets consolidés.'))
        )
      );
  }

  updateDocument(
    documentId: number,
    body: Record<string, unknown>
  ): Observable<Record<string, unknown>> {
    const url = `${lcApiBase(this.authService)}/api/documents/${documentId}`;
    return this.http
      .put<{ success: boolean; data?: Record<string, unknown>; error?: string }>(url, body)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Mise à jour impossible');
          }
          return res.data;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour du document.'))
        )
      );
  }

  finalizeFacture(
    factureId: number,
    payload: { lignes?: Record<string, unknown>[]; client_id?: number }
  ): Observable<Record<string, unknown>> {
    const url = `${lcApiBase(this.authService)}/api/documents/${factureId}/finalize-facture`;
    return this.http
      .post<{ success: boolean; data?: Record<string, unknown>; error?: string }>(url, payload)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Finalisation impossible');
          }
          return res.data;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors de la finalisation de la facture.'))
        )
      );
  }

  generateFactureFromTickets(
    documentIds: number[],
    stationId?: number
  ): Observable<{ facture_id: number; facture_code: string; ticket_ids: number[]; total: number }> {
    const session = this.authService.getSession();
    const url = `${lcApiBase(this.authService)}/api/documents/facture-batch`;
    const body = {
      document_ids: documentIds,
      station_id: stationId ?? this.authService.getBackStationId() ?? null,
    };
    return this.http
      .post<{
        success: boolean;
        data?: { facture_id: number; facture_code: string; ticket_ids: number[]; total: number };
        error?: string;
      }>(url, body)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Génération facture impossible');
          }
          return res.data;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors de la génération de la facture.'))
        )
      );
  }

  /** POST /api/documents/:id/passation-caisse — envoi temps réel vers la caisse cible. */
  passationToCaisse(documentId: number, caisseId: number): Observable<Record<string, unknown>> {
    const url = `${lcApiBase(this.authService)}/api/documents/${documentId}/passation-caisse`;
    return this.http
      .post<{
        success: boolean;
        data?: Record<string, unknown>;
        message?: string;
        error?: string;
      }>(url, { caisse_id: caisseId })
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Passation en caisse impossible');
          }
          return res.data;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur lors de la passation en caisse.'))
        )
      );
  }

  private buildFilterParams(
    entrepotIds?: number[],
    attrFilters?: TicketsListFilters
  ): HttpParams {
    let params = new HttpParams();
    if (entrepotIds && entrepotIds.length > 0) {
      params = params.set('entrepot_ids', entrepotIds.join(','));
    }
    if (attrFilters?.origine) {
      params = params.set('origine', attrFilters.origine);
    }
    if (attrFilters?.reglement) {
      params = params.set('reglement', attrFilters.reglement);
    }
    if (attrFilters?.statut) {
      params = params.set('statut', attrFilters.statut);
    }
    if (attrFilters?.type) {
      params = params.set('type', attrFilters.type);
    }
    if (attrFilters?.sous_types?.length) {
      params = params.set('sous_types', attrFilters.sous_types.join(','));
    }
    if (attrFilters?.famille) {
      params = params.set('famille', attrFilters.famille);
    }
    if (attrFilters?.date_debut) {
      params = params.set('date_debut', attrFilters.date_debut);
    }
    if (attrFilters?.date_fin) {
      params = params.set('date_fin', attrFilters.date_fin);
    }
    if (attrFilters?.facture_client) {
      params = params.set('facture_client', '1');
    }
    if (attrFilters?.exclude_factured) {
      params = params.set('exclude_factured', '1');
    }
    if (attrFilters?.document_scope) {
      params = params.set('document_scope', attrFilters.document_scope);
    }
    if (attrFilters?.negoce_kind) {
      params = params.set('negoce_kind', attrFilters.negoce_kind);
    }
    if (attrFilters?.ticket_avoire) {
      params = params.set('ticket_avoire', '1');
    }
    if (attrFilters?.consolide_only) {
      params = params.set('consolide_only', '1');
    }
    if (attrFilters?.exclude_consolide) {
      params = params.set('exclude_consolide', '1');
    }
    if (attrFilters?.exclude_en_traitement) {
      params = params.set('exclude_en_traitement', '1');
    }
    if (attrFilters?.exclude_annule) {
      params = params.set('exclude_annule', '1');
    }
    return params;
  }
}
